import { z } from "zod";
import type { Scenario } from "./types";

const EffectSchema = z.object({
  metric: z.enum([
    "schedule",
    "budget",
    "quality",
    "teamTrust",
    "stakeholderTrust",
    "technicalDebt",
    "operationalRisk",
  ]),
  delta: z.number().int().min(-30).max(30),
});

const FutureEventSchema = z.object({
  eventId: z.string().min(1),
  eligibleOnDay: z.number().int().positive(),
});

const ChoiceSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  description: z.string().optional(),
  effects: z.array(EffectSchema).min(1),
  resultText: z.string().min(1),
  schedules: z.array(FutureEventSchema).optional(),
});

const ProjectEventSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  sender: z.string().min(1),
  day: z.string().min(1),
  body: z.array(z.string().min(1)).min(1),
  eligibleWhen: z.function(),
  choices: z.array(ChoiceSchema).min(2),
});

const MetricsSchema = z.object({
  schedule: z.number().min(0).max(100),
  budget: z.number().min(0).max(100),
  quality: z.number().min(0).max(100),
  teamTrust: z.number().min(0).max(100),
  stakeholderTrust: z.number().min(0).max(100),
  technicalDebt: z.number().min(0).max(100),
  operationalRisk: z.number().min(0).max(100),
});

const ScenarioSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  premise: z.array(z.string().min(1)).min(1),
  initialMetrics: MetricsSchema,
  events: z.array(ProjectEventSchema).min(1),
  outcomes: z.array(
    z.object({ id: z.string().min(1), label: z.string().min(1), when: z.function(), summary: z.string().min(1) })
  ).min(1),
  endWhen: z.function(),
});

/**
 * Fail fast on malformed authored content — the gap the reference repo left
 * unwired (schemas defined, `.parse()` never called anywhere in its source).
 */
export function validateScenario(scenario: Scenario): Scenario {
  const result = ScenarioSchema.safeParse(scenario);
  if (!result.success) {
    throw new Error(
      `Invalid scenario "${scenario.id}": ${result.error.issues
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join("; ")}`
    );
  }

  const eventIds = new Set(scenario.events.map((e) => e.id));
  const choiceIds = new Set<string>();
  for (const event of scenario.events) {
    for (const choice of event.choices) {
      if (choiceIds.has(choice.id)) {
        throw new Error(`Invalid scenario "${scenario.id}": duplicate choice id "${choice.id}"`);
      }
      choiceIds.add(choice.id);
      for (const scheduled of choice.schedules ?? []) {
        if (!eventIds.has(scheduled.eventId)) {
          throw new Error(
            `Invalid scenario "${scenario.id}": choice "${choice.id}" schedules unknown event "${scheduled.eventId}"`
          );
        }
      }
    }
  }

  return scenario;
}
